<script lang="ts">
  import { onMount } from 'svelte';
  import { location } from 'svelte-spa-router';
  import BackToHomeButton from '../components/BackToHomeButton.svelte';
  import BackToTopButton from '../components/BackToTopButton.svelte';
  import CodeBlock from '../components/CodeBlock.svelte';
  import AnimatedBackground from '../lib/AnimatedBackground.svelte';
  import Footer from '../lib/Footer.svelte';
  
  type ContentBlock = {
    type: 'paragraph' | 'heading' | 'code';
    text: string;
    language?: string;
  };
  
  type Article = {
    title: string;
    date: string;
    readTime: string;
    content: ContentBlock[];
    tags: string[];
  };
  
  let article: Article = {
    title: '',
    date: '',
    readTime: '',
    content: [],
    tags: []
  };
  
  const blogData: Record<string, Article> = {
    'welcome-to-enkei64-portfolio': {
      title: "Welcome to enkei64's portfolio!",
      date: "June 8, 2025",
      readTime: "2 min read",
      tags: ["Portfolio", "Development", "Introduction"],
      content: [
        {
          type: 'paragraph',
          text: "Welcome to my personal portfolio! I'm enkei64, a Web & App Developer passionate about creating beautiful, functional digital experiences. In this post, I'll share a bit about how I built this site and my approach to web development."
        },
        {
          type: 'heading',
          text: "The Stack Behind This Site"
        },
        {
          type: 'paragraph',
          text: "This portfolio is built with modern web technologies, focusing on performance and aesthetic design. I've used Svelte as the core framework due to its lightweight nature and excellent developer experience."
        },
        {
          type: 'code',
          language: 'javascript',
          text: `
// Example of a Svelte component
import { onMount } from 'svelte';

let mounted = false;

onMount(() => {
  mounted = true;
  console.log('Component mounted and ready!');
});
          `
        },
        {
          type: 'paragraph',
          text: "The animations are kept subtle but effective, using CSS transitions and Svelte's built-in animation capabilities to create a smooth, engaging experience without overwhelming the user."
        },
        {
          type: 'heading',
          text: "Design Philosophy"
        },
        {
          type: 'paragraph',
          text: "When designing this portfolio, I focused on creating a clean, modern aesthetic with a touch of personality. The glass-morphism effect gives depth to the UI while maintaining readability and focus on the content."
        },
        {
          type: 'code',
          language: 'css',
          text: `
/* Glass effect styling */
.glass {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}
          `
        },
        {
          type: 'paragraph',
          text: "The responsive design ensures that the site looks great on all devices, from mobile phones to large desktop screens. I've carefully considered the typography, spacing, and color scheme to create a cohesive and pleasing visual experience."
        },
        {
          type: 'heading',
          text: "Future Plans"
        },
        {
          type: 'paragraph',
          text: "This portfolio is just the beginning! I plan to regularly update the blog with tutorials, insights on development, and updates on my projects. Stay tuned for more content, and feel free to reach out if you have any questions or would like to collaborate."
        }
      ]
    },
    'using-nugget-mobilegestalt-tweaks': {
      title: "Using Nugget MobileGestalt tweaks",
      date: "June 8, 2025",
      readTime: "3 min read",
      tags: ["iOS", "Tweaks", "MobileGestalt"],
      content: [
        {
          type: 'paragraph',
          text: "The MobileGestalt file (com.apple.MobileGestalt.plist) is a Property List (plist) file available on iOS devices. It contains obfuscated keys that indicate device capabilities that may be limited to certain models, such as Stage Manager support, Always On Display, Dynamic Island, Apple Intelligence, etc."
        },
        {
          type: 'heading',
          text: "Step by Step Guide"
        },
        {
          type: 'paragraph',
          text: "Here's how to extract and use the MobileGestalt file:"
        },
        {
          type: 'paragraph',
          text: "1. Download this Shortcut on iOS: <a href='https://www.icloud.com/shortcuts/e2077174cc424253a24164a1df674ac4' target='_blank' rel='noopener noreferrer'>Save MobileGestalt Shortcut</a>"
        },
        {
          type: 'paragraph',
          text: "2. Open the Shortcuts app and launch the Save MobileGestalt shortcut."
        },
        {
          type: 'paragraph',
          text: "3. If asked for permission to access Files, allow it."
        },
        {
          type: 'paragraph',
          text: "4. A file picker dialog will pop up. Just select a folder on your phone, such as Downloads, and press the Open button at the top."
        },
        {
          type: 'paragraph',
          text: "5. The MobileGestalt file should have been saved there as com.apple.MobileGestalt.plist."
        },
        {
          type: 'paragraph',
          text: "After saving the file, you can share it to various apps like e-mail or AirDrop to copy it over to your PC and use it in Nugget."
        },
        {
          type: 'heading',
          text: "Using the Nugget Helper Tool"
        },
        {
          type: 'paragraph',
          text: "For easier analysis and implementation of MobileGestalt tweaks, you can use the Nugget Helper Tool made by Squair. This tool simplifies the process of working with the complex MobileGestalt keys."
        },
        {
          type: 'paragraph',
          text: "Find the Nugget Helper Tool here: <a href='https://squair.is-a.dev/nugget-docs/' target='_blank' rel='noopener noreferrer'>https://squair.is-a.dev/nugget-docs/</a>"
        },
        {
          type: 'paragraph',
          text: "The Nugget Helper Tool offers several advantages:"
        },
        {
          type: 'paragraph',
          text: "• Shows you exactly which tweaks are supported on your specific iOS version"
        },
        {
          type: 'paragraph',
          text: "• Provides comprehensive installation guides for multiple platforms:"
        },
        {
          type: 'paragraph',
          text: "  - Mac"
        },
        {
          type: 'paragraph',
          text: "  - Windows"
        },
        {
          type: 'paragraph',
          text: "  - iPhone"
        },
        {
          type: 'paragraph',
          text: "  - iPad"
        },
        {
          type: 'paragraph',
          text: "• Offers a user-friendly interface for examining the MobileGestalt file"
        },
        {
          type: 'paragraph',
          text: "• Helps identify the specific keys you might want to modify for enabling features on your device"
        },
        {
          type: 'heading',
          text: "Common Use Cases"
        },
        {
          type: 'paragraph',
          text: "MobileGestalt tweaks can be used to enable features normally restricted to certain device models, such as:"
        },
        {
          type: 'paragraph',
          text: "• Stage Manager support on older iPads"
        },
        {
          type: 'paragraph',
          text: "• Always On Display features on non-iPhone 14 Pro models"
        },
        {
          type: 'paragraph',
          text: "• Dynamic Island features on non-iPhone 14 Pro models"
        },
        {
          type: 'paragraph',
          text: "• Apple Intelligence capabilities on unsupported devices"
        },
        {
          type: 'paragraph',
          text: "Remember that while modifying MobileGestalt values can enable restricted features, it doesn't guarantee they will work properly. Some features may depend on specific hardware capabilities that your device might not have."
        }
      ]
    }
  };
  
  let mounted = false;
  
  onMount(() => {
    mounted = true;
    
    window.scrollTo(0, 0);
    
    const path = $location.split('/');
    const slug = path[path.length - 1];
    
    if (blogData[slug]) {
      article = blogData[slug];
      document.title = `${article.title} | enkei64 Blog`;
    }
  });
</script>

<AnimatedBackground />
<BackToHomeButton />

<main class="blog-post-page">
  <div class="container">
    {#if article.title}
      <article class="post-content glass-card" class:animate={mounted}>
        <header class="post-header">
          <h1>{article.title}</h1>
          
          <div class="post-meta">
            <span class="post-date">{article.date}</span>
            <span class="post-read-time">{article.readTime}</span>
          </div>
          
          <div class="post-tags">
            {#each article.tags as tag}
              <span class="post-tag">{tag}</span>
            {/each}
          </div>
        </header>
        
        <div class="post-body">
          {#each article.content as block, index}
            {#if block.type === 'paragraph'}
              <p>{@html block.text}</p>
            {:else if block.type === 'heading'}
              <h2>{block.text}</h2>
            {:else if block.type === 'code'}
              <CodeBlock code={block.text} language={block.language} />
            {/if}
          {/each}
        </div>
      </article>
    {:else}
      <div class="error-message glass-card">
        <h2>Article not found</h2>
        <p>Sorry, the article you're looking for doesn't exist or has been moved.</p>
        <a href="/#/blog" class="btn">Back to Blog</a>
      </div>
    {/if}
  </div>
</main>

<Footer />
<BackToTopButton />

<style>
  .blog-post-page {
    min-height: 100vh;
    padding: var(--spacing-2xl) 0;
  }
  
  .container {
    max-width: 900px;
    width: 100%;
    margin: 0 auto;
    padding: 0 var(--spacing-lg);
  }
  
  .post-content {
    padding: var(--spacing-xl);
    margin-bottom: var(--spacing-xl);
  }
  
  .post-header {
    margin-bottom: var(--spacing-xl);
    border-bottom: 1px solid var(--glass-border);
    padding-bottom: var(--spacing-lg);
  }
  
  .post-header h1 {
    margin-bottom: var(--spacing-md);
    font-size: 2.5rem;
    line-height: 1.2;
  }
  
  .post-meta {
    display: flex;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-md);
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
  
  .post-tags {
    display: flex;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
  }
  
  .post-tag {
    padding: 0.25rem 0.75rem;
    background: rgba(255, 107, 53, 0.1);
    border: 1px solid rgba(255, 107, 53, 0.2);
    border-radius: 20px;
    font-size: 0.75rem;
    color: var(--accent-orange);
  }
  
  .post-body {
    font-size: 1.125rem;
    line-height: 1.8;
  }
  
  .post-body p {
    margin-bottom: var(--spacing-md);
    color: var(--text-secondary);
  }
  
  .post-body p :global(a) {
    color: var(--text-secondary);
    text-decoration: underline;
    transition: color 0.3s ease;
  }
  
  .post-body p :global(a:hover) {
    color: var(--accent-orange);
  }
  
  .post-body h2 {
    margin: var(--spacing-xl) 0 var(--spacing-md);
    font-size: 1.75rem;
  }
  
  .error-message {
    text-align: center;
    padding: var(--spacing-xl);
  }
  
  .error-message h2 {
    margin-bottom: var(--spacing-md);
  }
  
  .error-message p {
    margin-bottom: var(--spacing-lg);
    color: var(--text-secondary);
  }
  
  .animate {
    animation: fadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    opacity: 0;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    .post-content {
      padding: var(--spacing-lg);
    }
    
    .post-header h1 {
      font-size: 2rem;
    }
    
    .post-body {
      font-size: 1rem;
    }
  }
</style> 